<template>
  <v-container fluid class="printer-page">
    <section class="printer-management">
      <!-- Printers list -->
      <v-card rounded="lg" outlined>
        <v-card-text class="pb-2">
          <v-row align="center" class="printer-page__toolbar mb-4">
            <v-col cols="12" md="auto" class="flex-grow-1">
              <div class="text-h6">{{ $t("printer_management") }}</div>
              <div class="text-caption grey--text mt-1">
                {{ $t("printer_management_desc") }}
              </div>
            </v-col>
            <v-col
              cols="12"
              md="auto"
              class="d-flex flex-wrap justify-start justify-md-end printer-page__toolbar-actions"
            >
              <v-btn
                outlined
                color="primary"
                depressed
                class="printer-page__toolbar-btn mr-2 rounded-lg"
                @click="showPrinterTestDialog = true"
                :aria-label="$t('printer_test')"
              >
                <v-icon left small class="mr-1">fal fa-print</v-icon>
                {{ $t("printer_test") }}
              </v-btn>
              <v-btn
                depressed
                color="primary"
                class="printer-page__toolbar-btn rounded-lg"
                @click="openCreatePrinter"
                :aria-label="$t('add_printer')"
              >
                <v-icon left small class="mr-1">fal fa-plus-circle</v-icon>
                {{ $t("add_printer") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="printers"
            :loading="loading"
            :loading-text="$t('loading')"
            :no-data-text="$t('no_data')"
            class="elevation-0 printer-page__table"
            fixed-header
            dense
            hide-default-footer
          >
            <template slot="progress">
              <v-progress-linear
                color="primary"
                indeterminate
              ></v-progress-linear>
            </template>

            <template v-slot:[`item.name`]="{ item }">
              <div class="d-flex align-center py-1">
                <span
                  class="printer-page__status-dot mr-3 flex-shrink-0"
                  :class="
                    item.connection_status === 'online'
                      ? 'printer-page__status-dot--online'
                      : 'printer-page__status-dot--offline'
                  "
                  :title="
                    item.connection_status === 'online'
                      ? $t('online')
                      : $t('offline')
                  "
                  aria-hidden="true"
                />
                <div class="printer-page__name-text">
                  <div class="font-weight-medium text-truncate">
                    {{ item.name }}
                  </div>
                  <div class="text-caption grey--text text-truncate">
                    {{ item.printer_model || $t("printer_model") }}
                  </div>
                </div>
              </div>
            </template>

            <template v-slot:[`item.type`]="{ item }">
              <div>
                <v-chip
                  small
                  label
                  outlined
                  :color="getTypeColor(item.type)"
                  class="font-weight-medium"
                >
                  <v-icon x-small left>{{ getTypeIcon(item.type) }}</v-icon>
                  {{ $t(item.type) }}
                </v-chip>
                <div
                  v-if="getConnectionInfo(item)"
                  class="text-caption grey--text mt-1 text-truncate"
                  style="max-width: 200px"
                >
                  {{ getConnectionInfo(item) }}
                </div>
              </div>
            </template>

            <template v-slot:[`item.paper_size`]="{ item }">
              <span class="text-body-2">{{ item.paper_size }}</span>
            </template>

            <template v-slot:[`item.is_default_kitchen`]="{ item }">
              <v-chip
                v-if="item.is_default_kitchen"
                small
                label
                outlined
                color="primary"
              >
                {{ $t("default_kitchen") }}
              </v-chip>
              <span v-else class="grey--text text-caption">—</span>
            </template>

            <template v-slot:[`item.print_category`]="{ item }">
              <v-chip
                v-if="item.print_category"
                small
                label
                outlined
                color="grey"
              >
                {{ $t(item.print_category) }}
              </v-chip>
              <span v-else class="grey--text text-caption">—</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex justify-end">
                <v-menu bottom left offset-y content-class="printer-page__menu">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      depressed
                      class="printer-page__icon-btn"
                      v-bind="attrs"
                      v-on="on"
                      :aria-label="$t('actions')"
                    >
                      <v-icon small>fal fa-ellipsis-v</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense nav class="py-1">
                    <v-list-item
                      @click="testConnection(item)"
                      :disabled="!!item.testing"
                    >
                      <v-list-item-icon class="mr-3 my-2">
                        <v-icon small color="primary">
                          fal fa-test-connection
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{
                          $t("test_connection")
                        }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="item.testing">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="primary"
                        />
                      </v-list-item-action>
                    </v-list-item>
                    <v-list-item
                      @click="openTestPrintDialog(item)"
                      :disabled="!!item.testPrinting"
                    >
                      <v-list-item-icon class="mr-3 my-2">
                        <v-icon small color="primary"> fal fa-print </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{
                          $t("test_print")
                        }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action v-if="item.testPrinting">
                        <v-progress-circular
                          indeterminate
                          size="18"
                          width="2"
                          color="primary"
                        />
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider class="my-1" />
                    <v-list-item @click="editPrinter(item)">
                      <v-list-item-icon class="mr-3 my-2">
                        <v-icon small color="primary">fal fa-edit</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t("edit") }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                      @click="deletePrinter(item)"
                      class="error--text"
                    >
                      <v-list-item-icon class="mr-3 my-2">
                        <v-icon small color="error">fal fa-trash</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="error--text">{{
                          $t("delete")
                        }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Printer Dialog -->
      <app-custom-dialog
        v-model="showAddPrinterDialog"
        :title="editingPrinter ? 'edit_printer' : 'add_printer'"
        :footer="true"
        width="600px"
        :submit-text="editingPrinter ? 'update' : 'save'"
        @submit="savePrinter"
      >
        <div slot="activator">
          <v-form ref="printerForm" v-model="validForm">
            <v-row>
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium">
                  {{ $t("basic_information") }}
                </div>
                <v-divider class="mt-2 mb-2" />
              </v-col>

              <v-col cols="12" md="6">
                <TTextField
                  v-model="printerForm.name"
                  label="printer_name"
                  :error-messages="validatePrinterName()"
                />
              </v-col>

              <v-col cols="12" md="6">
                <TextSelect
                  v-model="printerForm.type"
                  :items="printerTypes"
                  label="printer_type"
                  text="text"
                  item-value="value"
                  :error-messages="validatePrinterType()"
                />
              </v-col>

              <v-col cols="12" md="6">
                <TextSelect
                  v-model="printerForm.printer_brand"
                  :items="printerBrands"
                  label="printer_brand"
                  text="text"
                  item-value="value"
                  :error-messages="[]"
                />
              </v-col>

              <v-col cols="12" md="6">
                <TTextField
                  v-model="printerForm.printer_model"
                  label="printer_model"
                  :error-messages="[]"
                />
              </v-col>

              <v-col cols="12" md="6">
                <TextSelect
                  v-model="printerForm.paper_size"
                  :items="paperSizes"
                  label="paper_size"
                  text="text"
                  item-value="value"
                  :error-messages="[]"
                />
              </v-col>

              <!-- Connection Configuration -->
              <v-col
                cols="12"
                v-if="
                  printerForm.type === 'ethernet' || printerForm.type === 'wifi'
                "
              >
                <div class="text-subtitle-2 font-weight-medium mt-2">
                  {{ $t("network_configuration") }}
                </div>
                <v-divider class="mt-2 mb-2" />
              </v-col>

              <v-col
                cols="12"
                md="8"
                v-if="
                  printerForm.type === 'ethernet' || printerForm.type === 'wifi'
                "
              >
                <TTextField
                  v-model="printerForm.ip_address"
                  label="ip_address"
                  :error-messages="getIpErrorMessages()"
                />
              </v-col>

              <v-col
                cols="12"
                md="4"
                v-if="
                  printerForm.type === 'ethernet' || printerForm.type === 'wifi'
                "
              >
                <TTextField
                  v-model="printerForm.port"
                  label="port"
                  type="number"
                  :error-messages="getPortErrorMessages()"
                />
              </v-col>

              <!-- USB Configuration -->
              <v-col cols="12" v-if="printerForm.type === 'usb'">
                <div class="text-subtitle-2 font-weight-medium mt-2">
                  {{ $t("usb_configuration") }}
                </div>
                <v-divider class="mt-2 mb-2" />
              </v-col>

              <v-col cols="12" v-if="printerForm.type === 'usb'">
                <v-btn
                  block
                  outlined
                  @click="scanUSBDevices"
                  :loading="scanningUSB"
                >
                  <v-icon left>fal fa-search</v-icon>
                  {{ $t("scan_usb_devices") }}
                </v-btn>
              </v-col>

              <v-col
                cols="12"
                v-if="printerForm.type === 'usb' && usbDevices.length > 0"
              >
                <TextSelect
                  v-model="printerForm.usb_device_id"
                  :items="usbDevices"
                  label="select_usb_device"
                  text="name"
                  item-value="id"
                  :error-messages="validateUSBDevice()"
                />
              </v-col>

              <!-- Auto Discovery -->
              <v-col cols="12">
                <v-btn
                  block
                  color="primary"
                  outlined
                  @click="autoDiscoverPrinters"
                  :loading="discoveringPrinters"
                >
                  <v-icon left>fal fa-radar</v-icon>
                  {{ $t("auto_discover_printers") }}
                </v-btn>
              </v-col>

              <!-- Print Configuration -->
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mt-2">
                  {{ $t("print_configuration") }}
                </div>
                <v-divider class="mt-2 mb-2" />
              </v-col>

              <v-col cols="12">
                <CheckBox
                  v-model="printerForm.is_default_kitchen"
                  label="default_kitchen_printer"
                />
              </v-col>

              <v-col cols="12">
                <TextSelect
                  v-model="printerForm.print_category"
                  :items="printCategories"
                  label="print_categories"
                  text="text"
                  item-value="value"
                  :error-messages="validatePrintCategory()"
                />
              </v-col>
            </v-row>
          </v-form>
        </div>
      </app-custom-dialog>

      <!-- Print Jobs Dialog -->
      <app-custom-dialog
        v-model="showPrintJobsDialog"
        title="print_jobs"
        :footer="false"
        width="800px"
      >
        <div slot="activator">
          <v-data-table
            :headers="printJobHeaders"
            :items="printJobs"
            :loading="loadingPrintJobs"
            class="elevation-0"
            dense
          >
            <!-- Status -->
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                :color="getPrintJobStatusColor(item.status)"
                outlined
                small
                label
                class="font-weight-medium"
              >
                {{ $t(item.status) }}
              </v-chip>
            </template>

            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="item.status === 'failed'"
                icon
                class="printer-page__icon-btn"
                @click="retryPrintJob(item)"
                :loading="item.retrying"
                :aria-label="$t('try_again')"
              >
                <v-icon small>fal fa-redo</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="showPrintJobsDialog = false">
              {{ $t("close") }}
            </v-btn>
          </v-card-actions>
        </div>
      </app-custom-dialog>

      <!-- Test Print Dialog -->
      <app-custom-dialog
        v-model="showTestPrintDialog"
        title="test_print"
        title-icon="fal fa-print"
        title-icon-color="primary"
        :footer="true"
        width="560px"
        submit-text="print_test"
        @submit="executeTestPrint"
      >
        <div slot="activator">
          <v-form ref="testPrintForm" v-model="testPrintValid">
            <v-row>
              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium">
                  {{ $t("printer_information") }}
                </div>
                <v-divider class="mt-2 mb-2" />
                <div class="printer-page__dl">
                  <div class="printer-page__dl-row">
                    <span class="grey--text">{{ $t("printer_name") }}</span>
                    <span class="font-weight-medium">{{
                      selectedPrinter?.name
                    }}</span>
                  </div>
                  <div class="printer-page__dl-row">
                    <span class="grey--text">{{ $t("printer_type") }}</span>
                    <span>{{ selectedPrinter?.type }}</span>
                  </div>
                  <div class="printer-page__dl-row align-center">
                    <span class="grey--text">{{
                      $t("connection_status")
                    }}</span>
                    <v-chip
                      small
                      outlined
                      label
                      :color="
                        selectedPrinter?.connection_status === 'online'
                          ? 'success'
                          : 'error'
                      "
                      class="font-weight-medium ma-0"
                    >
                      {{
                        selectedPrinter?.connection_status
                          ? $t(selectedPrinter.connection_status)
                          : ""
                      }}
                    </v-chip>
                  </div>
                </div>
              </v-col>

              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mt-2">
                  {{ $t("test_options") }}
                </div>
                <v-divider class="mt-2 mb-2" />
              </v-col>

              <v-col cols="12">
                <TextSelect
                  v-model="selectedTestType"
                  :items="testTypes"
                  label="test_type"
                  text="text"
                  item-value="value"
                  :error-messages="[]"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-subtitle>{{
                          item.description
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </TextSelect>
              </v-col>

              <v-col cols="12">
                <CheckBox
                  v-model="includeTestInfo"
                  label="include_printer_info"
                />
              </v-col>

              <v-col
                cols="12"
                v-if="selectedPrinter?.connection_status === 'offline'"
              >
                <v-alert type="warning" text>
                  {{ $t("printer_offline_warning") }}
                </v-alert>
              </v-col>

              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>

              <v-col cols="12">
                <div class="text-subtitle-2 font-weight-medium mt-2">
                  {{ $t("what_test_will_print") }}
                </div>
                <v-divider class="mt-2 mb-3" />
                <div v-if="selectedTestType === 'basic'" class="mt-2">
                  <ul>
                    <li>{{ $t("basic_test_formatting") }}</li>
                    <li>{{ $t("font_sizes_and_alignment") }}</li>
                    <li>{{ $t("character_sets") }}</li>
                  </ul>
                </div>
                <div v-else-if="selectedTestType === 'receipt'" class="mt-2">
                  <ul>
                    <li>{{ $t("sample_receipt_format") }}</li>
                    <li>{{ $t("item_list_and_totals") }}</li>
                    <li>{{ $t("receipt_layout") }}</li>
                  </ul>
                </div>
                <div v-else-if="selectedTestType === 'kitchen'" class="mt-2">
                  <ul>
                    <li>{{ $t("sample_kitchen_order") }}</li>
                    <li>{{ $t("table_information") }}</li>
                    <li>{{ $t("order_items") }}</li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </div>
      </app-custom-dialog>
    </section>
    <!-- Printer Test Dialog -->
    <PrinterTestDialog v-model="showPrinterTestDialog" />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TTextField from "@/components/global/TextField.vue";
import TextSelect from "@/components/global/TextSelect.vue";
import CheckBox from "@/components/global/CheckBox.vue";
import { toast, confirm } from "@/controllers/alert";
import PrinterTestDialog from "@/components/PrinterTestDialog.vue";

export default {
  name: "PrinterManagement",
  components: {
    TTextField,
    TextSelect,
    CheckBox,
    PrinterTestDialog,
  },
  data() {
    return {
      loading: false,
      saving: false,
      scanningUSB: false,
      discoveringPrinters: false,
      showAddPrinterDialog: false,
      showPrintJobsDialog: false,
      showTestPrintDialog: false,
      showPrinterTestDialog: false,
      loadingPrintJobs: false,
      editingPrinter: null,
      validForm: false,
      testPrintValid: false,

      // Printer form
      printerForm: {
        name: "",
        type: "ethernet",
        ip_address: "",
        port: "9100",
        usb_device_id: "",
        printer_brand: "generic",
        printer_model: "",
        paper_size: "58mm",
        is_default_kitchen: false,
        print_category: "",
      },

      // Data
      printers: [],
      usbDevices: [],
      printJobs: [],

      // Test print data
      selectedPrinter: null,
      selectedTestType: "basic",
      includeTestInfo: true,

      // Configuration
      printerTypes: [
        { value: "ethernet", text: "ethernet", icon: "fal fa-network-wired" },
        { value: "wifi", text: "wifi", icon: "fal fa-wifi" },
        { value: "usb", text: "usb", icon: "fal fa-usb" },
      ],

      printerBrands: [
        { value: "generic", text: "Generic/Unknown" },
        { value: "epson", text: "EPSON" },
        { value: "zjiang", text: "Zjiang" },
        { value: "xprinter", text: "Xprinter" },
        { value: "citizen", text: "Citizen" },
        { value: "bixolon", text: "Bixolon" },
        { value: "custom", text: "Other" },
      ],

      testTypes: [
        {
          value: "basic",
          text: "Basic Test",
          description: "Simple connectivity test",
        },
        {
          value: "receipt",
          text: "Receipt Test",
          description: "Test receipt format",
        },
        {
          value: "kitchen",
          text: "Kitchen Test",
          description: "Test kitchen order format",
        },
      ],

      paperSizes: [
        { text: "58mm", value: "58mm" },
        { text: "80mm", value: "80mm" },
        { text: "A4", value: "A4" },
      ],
    };
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
    }),

    printCategories() {
      return [
        { text: this.$t("kitchen"), value: "kitchen" },
        { text: this.$t("bar"), value: "bar" },
        { text: this.$t("receipt"), value: "receipt" },
        { text: this.$t("order"), value: "order" },
      ];
    },

    headers() {
      return [
        { text: this.$t("printer_name"), value: "name", sortable: false },
        { text: this.$t("type_connection"), value: "type", sortable: false },
        { text: this.$t("paper_size"), value: "paper_size", sortable: false },
        // {
        //   text: this.$t("default_kitchen"),
        //   value: "is_default_kitchen",
        //   sortable: false,
        // },
        {
          text: this.$t("print_categories"),
          value: "print_category",
          sortable: false,
        },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "end",
          width: 72,
        },
      ];
    },

    printJobHeaders() {
      return [
        { text: this.$t("id"), value: "id" },
        { text: this.$t("printer"), value: "printer_name" },
        { text: this.$t("type"), value: "print_type" },
        { text: this.$t("status"), value: "status" },
        { text: this.$t("created_at"), value: "created_at" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          width: 80,
        },
      ];
    },
  },

  methods: {
    ...mapActions({}),

    // Validation rules
    requiredRule: function (value) {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return !!value || false;
    },
    ipRule: function (value) {
      if (!value) return true;
      const ipRegex =
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      return ipRegex.test(value);
    },
    portRule: function (value) {
      if (!value) return true;
      const port = parseInt(value);
      return port > 0 && port <= 65535;
    },
    getIpErrorMessages() {
      const errors = [];
      if (
        this.printerForm.type === "ethernet" ||
        this.printerForm.type === "wifi"
      ) {
        if (!this.printerForm.ip_address) {
          errors.push(this.$t("field_required"));
        } else if (!this.ipRule(this.printerForm.ip_address)) {
          errors.push(this.$t("invalid_ip_address"));
        }
      }
      return errors;
    },
    getPortErrorMessages() {
      const errors = [];
      if (
        this.printerForm.type === "ethernet" ||
        this.printerForm.type === "wifi"
      ) {
        if (!this.printerForm.port) {
          errors.push(this.$t("field_required"));
        } else if (!this.portRule(this.printerForm.port)) {
          errors.push(this.$t("invalid_port"));
        }
      }
      return errors;
    },
    validatePrinterName() {
      return this.printerForm.name ? [] : [this.$t("field_required")];
    },
    validatePrinterType() {
      return this.printerForm.type ? [] : [this.$t("field_required")];
    },
    validatePrintCategory() {
      return this.printerForm.print_category ? [] : [this.$t("field_required")];
    },

    async loadData() {
      this.loading = true;
      try {
        const { data } = await this.$http.get("/printers");

        if (data && data.status && data.data) {
          this.printers = data.data.map((printer) => ({
            ...printer,
            print_category: printer.print_category || "",
          }));
        } else {
          this.printers = [];
        }
      } catch (error) {
        console.error("Error loading printers:", error);
        toast(this, "error_loading_printers", "error");
      } finally {
        this.loading = false;
      }
    },

    openCreatePrinter() {
      this.editingPrinter = null;
      this.printerForm = {
        name: "",
        type: "ethernet",
        ip_address: "",
        port: "9100",
        usb_device_id: "",
        printer_brand: "generic",
        printer_model: "",
        paper_size: "58mm",
        is_default_kitchen: false,
        print_category: "",
      };
      this.usbDevices = [];
      this.showAddPrinterDialog = true;
    },

    async scanUSBDevices() {
      this.scanningUSB = true;
      try {
        // NOTE: This must be called directly from button click to maintain user gesture
        const printerService = new (
          await import("@/services/printerService.js")
        ).default(this.$http);
        const devices = await printerService.scanUSBDevices();

        this.usbDevices = devices;

        if (devices.length > 0) {
          toast(this, "usb_scan_complete", "success");
        } else {
          // Provide helpful guidance when no devices found
          toast(this, "usb_scan_no_devices", "warning");
        }
      } catch (error) {
        console.error("Error scanning USB:", error);
        // Show user-friendly error message
        const errorMessage = error.message || "usb_scan_error";
        if (
          errorMessage.includes("user action") ||
          errorMessage.includes("user gesture")
        ) {
          toast(this, "usb_scan_click_prompt", "warning");
        } else if (
          errorMessage.includes("permission") ||
          errorMessage.includes("connected")
        ) {
          toast(this, errorMessage, "warning");
        } else {
          toast(this, errorMessage, "error");
        }
      } finally {
        this.scanningUSB = false;
      }
    },

    async autoDiscoverPrinters() {
      this.discoveringPrinters = true;
      try {
        const printerService = new (
          await import("@/services/printerService.js")
        ).default(this.$http);
        const discoveryResult = await printerService.autoDiscoverPrinters();

        // Combine all discovered devices
        const allDevices = [...discoveryResult.usb, ...discoveryResult.network];

        // Show informational messages (e.g., USB requires direct user action)
        if (discoveryResult.info && discoveryResult.info.length > 0) {
          discoveryResult.info.forEach((info) => {
            toast(this, info, "info");
          });
        }

        if (allDevices.length > 0) {
          toast(
            this,
            `${this.$t("discovery_complete")} (${allDevices.length} ${this.$t("devices_found_suffix")})`,
            "success",
          );

          // Add discovered devices to the appropriate lists
          this.usbDevices = discoveryResult.usb;
        } else {
          // No devices found, show helpful message
          toast(this, "discovery_no_printers", "info");
        }

        if (discoveryResult.errors.length > 0) {
          console.warn("Discovery errors:", discoveryResult.errors);
          // Show errors that aren't just skipped USB
          const realErrors = discoveryResult.errors.filter(
            (e) => !e.includes("skipped"),
          );
          if (realErrors.length > 0) {
            toast(this, realErrors.join(", "), "warning");
          }
        }
      } catch (error) {
        console.error("Error during auto-discovery:", error);
        toast(this, "auto_discovery_failed", "error");
      } finally {
        this.discoveringPrinters = false;
      }
    },

    editPrinter(printer) {
      this.editingPrinter = printer;
      this.printerForm = {
        name: printer.name || "",
        type: printer.type || "ethernet",
        ip_address: printer.ip_address || "",
        port: printer.port || "9100",
        usb_device_id: printer.usb_device_id || "",
        printer_brand: printer.printer_brand || "generic",
        printer_model: printer.printer_model || "",
        paper_size: printer.paper_size || "58mm",
        is_default_kitchen: !!printer.is_default_kitchen,
        print_category: printer.print_category || "",
      };
      this.showAddPrinterDialog = true;
    },

    async deletePrinter(printer) {
      const confirmed = await confirm({
        props: this,
        title: "delete_printer",
        text: "delete_printer_confirm",
      });

      if (!confirmed) return;

      try {
        const { data } = await this.$http.delete(`/printers/${printer.id}`);

        if (data && data.status) {
          this.printers = this.printers.filter((p) => p.id !== printer.id);
          toast(this, "printer_deleted", "success");
        }
      } catch (error) {
        console.error("Error deleting printer:", error);
        toast(this, "error_deleting_printer", "error");
      }
    },

    async savePrinter() {
      if (!this.printerForm.name) {
        toast(this, "field_required", "error");
        return;
      }
      if (!this.printerForm.type) {
        toast(this, "field_required", "error");
        return;
      }
      if (!this.printerForm.print_category) {
        toast(this, "field_required", "error");
        return;
      }
      if (
        (this.printerForm.type === "ethernet" ||
          this.printerForm.type === "wifi") &&
        (!this.printerForm.ip_address ||
          !this.ipRule(this.printerForm.ip_address))
      ) {
        toast(this, "invalid_ip_address", "error");
        return;
      }
      if (
        (this.printerForm.type === "ethernet" ||
          this.printerForm.type === "wifi") &&
        (!this.printerForm.port || !this.portRule(this.printerForm.port))
      ) {
        toast(this, "invalid_port", "error");
        return;
      }

      this.saving = true;
      try {
        const apiData = {
          name: this.printerForm.name,
          type: this.printerForm.type,
          printer_model: this.printerForm.printer_model || null,
          printer_brand: this.printerForm.printer_brand || null,
          paper_size: this.printerForm.paper_size || "58mm",
          is_default_kitchen: this.printerForm.is_default_kitchen,
          print_category: this.printerForm.print_category || "all",
          connection_status: "offline",
        };

        if (
          this.printerForm.type === "ethernet" ||
          this.printerForm.type === "wifi"
        ) {
          apiData.ip_address = this.printerForm.ip_address;
          apiData.port = parseInt(this.printerForm.port);
        }

        if (this.editingPrinter) {
          await this.$http.put(`/printers/${this.editingPrinter.id}`, apiData);
          toast(this, "printer_updated", "success");
        } else {
          await this.$http.post("/printers", apiData);
          toast(this, "printer_created", "success");
        }

        await this.loadData();

        this.closePrinterDialog();
      } catch (error) {
        console.error("Error saving printer:", error);
        toast(this, "error_saving_printer", "error");
      } finally {
        this.saving = false;
      }
    },

    closePrinterDialog() {
      this.showAddPrinterDialog = false;
      this.editingPrinter = null;
      this.printerForm = {
        name: "",
        type: "ethernet",
        ip_address: "",
        port: "9100",
        usb_device_id: "",
        printer_brand: "generic",
        printer_model: "",
        paper_size: "58mm",
        is_default_kitchen: false,
        print_category: "",
      };
      this.usbDevices = [];
    },

    async loadPrintJobs() {
      this.loadingPrintJobs = true;
      try {
        // Mock print jobs data
        this.printJobs = [
          {
            id: 1,
            printer_name: "Kitchen Printer 1",
            print_type: "kitchen",
            status: "completed",
            created_at: new Date().toISOString(),
          },
          {
            id: 2,
            printer_name: "Bar Printer",
            print_type: "bar",
            status: "pending",
            created_at: new Date().toISOString(),
          },
        ];
      } catch (error) {
        console.error("Error loading print jobs:", error);
        toast(this, "error_loading_print_jobs", "error");
      } finally {
        this.loadingPrintJobs = false;
      }
    },

    async retryPrintJob(job) {
      this.$set(job, "retrying", true);
      try {
        // Mock retry - simulate delay
        await new Promise((resolve) => setTimeout(resolve, 1000));
        job.status = "completed";
        toast(this, "print_job_retried", "success");
      } catch (error) {
        console.error("Error retrying print job:", error);
        toast(this, "error_retrying_print_job", "error");
      } finally {
        this.$set(job, "retrying", false);
      }
    },

    // Helper methods
    getTypeColor(type) {
      const colors = {
        ethernet: "blue",
        wifi: "green",
        usb: "orange",
      };
      return colors[type] || "grey";
    },

    getTypeIcon(type) {
      const icons = {
        ethernet: "fal fa-network-wired",
        wifi: "fal fa-wifi",
        usb: "fal fa-usb",
      };
      return icons[type] || "fal fa-print";
    },

    getConnectionInfo(printer) {
      if (printer.type === "ethernet" || printer.type === "wifi") {
        return `${printer.ip_address}:${printer.port}`;
      }
      return "";
    },

    getPrintJobStatusColor(status) {
      const colors = {
        pending: "orange",
        printing: "blue",
        completed: "success",
        failed: "error",
      };
      return colors[status] || "grey";
    },

    // Test print methods
    openTestPrintDialog(printer) {
      this.selectedPrinter = printer;
      this.selectedTestType = "basic";
      this.includeTestInfo = true;
      this.showTestPrintDialog = true;
    },

    async executeTestPrint() {
      if (!this.selectedPrinter) return;

      try {
        this.$set(this.selectedPrinter, "testPrinting", true);

        const printerService = new (
          await import("@/services/printerService.js")
        ).default(this.$http);
        const result = await printerService.printTestPage(
          this.selectedPrinter,
          this.selectedTestType,
        );

        if (result.success) {
          toast(this, "test_print_successful", "success");
          this.showTestPrintDialog = false;
        } else {
          toast(this, result.message || this.$t("test_print_failed"), "error");
        }
      } catch (error) {
        console.error("Test print error:", error);
        toast(this, "test_print_failed", "error");
      } finally {
        this.$set(this.selectedPrinter, "testPrinting", false);
      }
    },

    // Validation methods
    validateUSBDevice() {
      if (this.printerForm.type === "usb" && this.usbDevices.length > 0) {
        return this.printerForm.usb_device_id
          ? []
          : [this.$t("field_required")];
      }
      return [];
    },

    async testConnectionWithEnhancedService(printer) {
      this.$set(printer, "testing", true);
      try {
        const printerService = new (
          await import("@/services/printerService.js")
        ).default(this.$http);
        const result = await printerService.testConnection(printer);

        if (result.success) {
          printer.connection_status = "online";
          toast(this, "printer_connected", "success");
        } else {
          printer.connection_status = "offline";
          toast(this, "printer_connection_failed", "error");
        }
      } catch (error) {
        console.error("Error testing connection:", error);
        printer.connection_status = "offline";
        toast(this, "connection_test_error", "error");
      } finally {
        this.$set(printer, "testing", false);
      }
    },

    // Enhanced connection testing
    async testConnection(printer) {
      if (["usb"].includes(printer.type)) {
        return await this.testConnectionWithEnhancedService(printer);
      }

      // Keep original implementation for network printers
      this.$set(printer, "testing", true);
      try {
        const { data } = await this.$http.post(
          `/printers/${printer.id}/test_connection`,
          null,
        );

        if (data && data.status && data.data) {
          const success = data.data.success;
          printer.connection_status = success ? "online" : "offline";
          if (success) {
            toast(this, "printer_connected", "success");
          } else {
            toast(this, "printer_connection_failed", "error");
          }
        }
      } catch (error) {
        console.error("Error testing connection:", error);
        printer.connection_status = "offline";
        toast(this, "connection_test_error", "error");
      } finally {
        this.$set(printer, "testing", false);
      }
    },
  },

  async created() {
    await this.loadData();
  },
};
</script>

<style scoped>
.printer-management {
  max-width: 1400px;
  margin: 0 auto;
}

.printer-page__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.printer-page__status-dot--online {
  background-color: #4caf50;
}

.printer-page__status-dot--offline {
  background-color: #f44336;
}

.printer-page__name-text {
  min-width: 0;
}

.printer-page__toolbar-actions .printer-page__toolbar-btn {
  min-height: 44px;
}

.printer-page__table >>> thead th {
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54) !important;
}

.theme--dark .printer-page__table >>> thead th {
  color: rgba(255, 255, 255, 0.55) !important;
}

.printer-page__icon-btn {
  min-width: 44px !important;
  min-height: 44px !important;
  border-radius: 8px !important;
}

.printer-page__icon-btn:focus-visible {
  outline: 2px solid var(--v-primary-base, #1976d2);
  outline-offset: 2px;
}

.printer-page__toolbar-btn:focus-visible {
  outline: 2px solid var(--v-primary-base, #1976d2);
  outline-offset: 2px;
}

.printer-page__dl {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.printer-page__dl-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.printer-page__dl-row span:first-child {
  flex-shrink: 0;
}
</style>
